import React from "react";
import { If, makeClassName } from "@hex-insights/core";
import { MessageAvailabilityPeriodTimingFragment, User } from "../../../../Utilities";
import { MessageAvailabilityPeriodUtilities } from "../../../Utilities";
import * as PersonComponents from "../../Person";
import styles from "./styles.module.css";

export type IconUser = Pick<User, "id" | "email"> & {
	person?: PersonComponents.IconPerson | null;
};

export type UserMultiIcon = Omit<PersonComponents.PersonMultiIconProps, "people"> & {
	users: IconUser[];
};

export function UserMultiIcon({ users, ...props }: UserMultiIcon) {
	const people = React.useMemo(() => users.map(userToPerson), [users]);

	return <PersonComponents.PersonMultiIcon people={people} {...props} />;
}

export type UserAdditionalCountIconProps = PersonComponents.PersonAdditionalCountIconProps;

export function UserAdditionalCountIcon(props: UserAdditionalCountIconProps) {
	return <PersonComponents.PersonAdditionalCountIcon {...props} />;
}

export type UserIconProps = Omit<PersonComponents.PersonIconProps, "person"> & {
	user: IconUser;
	status?: "available" | "unavailable" | "loading";
	statusText?: string;
	statusColor?: string;
	iconClassName?: string;
	iconStyle?: React.CSSProperties;
	statusClassName?: string;
	statusStyle?: React.CSSProperties;
};

export function UserIcon({
	user,
	status,
	statusText,
	statusColor,
	size = 32,
	className,
	style,
	iconClassName,
	iconStyle,
	statusClassName: statusClassNameProp,
	statusStyle: statusStyleProp,
	...props
}: UserIconProps) {
	const person = React.useMemo(() => userToPerson(user), [user]);

	const {
		title: statusTitle,
		className: statusClassName,
		style: statusStyle,
		withStatus = false,
	} = getStatusInfo(status, statusText, statusColor);

	return (
		<div
			className={makeClassName(styles["user-icon"], className)}
			style={{ "--size": `${size}px`, ...style } as React.CSSProperties}
		>
			<PersonComponents.PersonIcon person={person} size={size} className={iconClassName} style={iconStyle} {...props} />
			<If condition={withStatus}>
				<div
					title={statusTitle}
					className={makeClassName(styles["user-icon__status"], statusClassName, statusClassNameProp)}
					style={{ ...statusStyle, ...statusStyleProp }}
				/>
			</If>
		</div>
	);
}

export function userToPerson(user: IconUser) {
	if (user.person) {
		return user.person;
	}
	return { id: user.id, name: user.email, publicName: "User", imageURL: "", imageKey: "", imageContentType: "" };
}

function getStatusInfo(
	status: UserIconProps["status"],
	statusText: UserIconProps["statusText"],
	statusColor: UserIconProps["statusColor"],
) {
	if (status) {
		let title = "";
		switch (status) {
			case "available":
				title = "Available";
				break;
			case "unavailable":
				title = "Unavailable";
				break;
			case "loading":
				title = "Loading...";
		}

		return {
			title,
			className: styles[`user-icon__status--${status}`],
			withStatus: true,
		};
	}
	if (statusText && statusColor) {
		return {
			title: statusText,
			style: {
				"--status---color": statusColor,
			} as React.CSSProperties,
			withStatus: true,
		};
	}
	return {};
}

export type UserWithMessagingStatusIconProps = Omit<UserIconProps, "user" | "status" | "statusText" | "statusColor"> & {
	user: UserIconProps["user"] & {
		messageAvailabilityPeriods: MessageAvailabilityPeriodTimingFragment[];
	};
};

export function UserWithMessagingStatusIcon({ user, ...props }: UserWithMessagingStatusIconProps) {
	const status = React.useMemo(() => {
		if (user.messageAvailabilityPeriods.length === 0) {
			return undefined;
		}
		const isAvailable = MessageAvailabilityPeriodUtilities.isUserAvailable(user.messageAvailabilityPeriods);
		return isAvailable ? "available" : "unavailable";
	}, [user.messageAvailabilityPeriods]);

	return <UserIcon user={user} status={status} {...props} />;
}

export type UserIconWithFullModalProps = Omit<PersonComponents.PersonIconWithFullModalProps, "person"> & {
	user: IconUser;
};

export function UserIconWithFullModal({ user, ...props }: UserIconWithFullModalProps) {
	const person = React.useMemo(() => userToPerson(user), [user]);

	return <PersonComponents.PersonIconWithFullModal person={person} {...props} />;
}

export type UsersCondensedListProps = Omit<PersonComponents.PeopleCondensedListProps, "people"> & {
	users: IconUser[];
};

export function UsersCondensedList({ users, ...props }: UsersCondensedListProps) {
	const people = React.useMemo(() => users.map(userToPerson), [users]);

	return <PersonComponents.PeopleCondensedList people={people} {...props} />;
}
